import React, { useState } from 'react';
import { useDisplayNotifications, DisplayNotification } from '../../contexts/DisplayNotificationContext';
import { Link } from 'react-router-dom';

/**
 * Component for displaying display-related notifications
 */
const DisplayNotifications: React.FC = () => {
  const { notifications, unreadCount, markAllAsRead, clearNotification, clearAllNotifications } = useDisplayNotifications();
  const [isOpen, setIsOpen] = useState(false);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
    if (!isOpen && unreadCount > 0) {
      // Mark all as read when opening the dropdown
      markAllAsRead();
    }
  };

  // Format the timestamp
  const formatTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    
    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffMinutes < 1440) {
      const diffHours = Math.floor(diffMinutes / 60);
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else {
      const diffDays = Math.floor(diffMinutes / 1440);
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }
  };

  // Get icon for notification type
  const getIcon = (type: DisplayNotification['type']): string => {
    switch (type) {
      case 'success':
        return 'fas fa-check-circle text-success';
      case 'warning':
        return 'fas fa-exclamation-triangle text-warning';
      case 'error':
        return 'fas fa-times-circle text-danger';
      case 'info':
      default:
        return 'fas fa-info-circle text-info';
    }
  };

  return (
    <div className="dropdown">
      <button 
        className="btn btn-link position-relative" 
        type="button" 
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
      >
        <i className="fas fa-bell fs-5"></i>
        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {unreadCount > 99 ? '99+' : unreadCount}
            <span className="visually-hidden">unread notifications</span>
          </span>
        )}
      </button>
      
      <div 
        className={`dropdown-menu dropdown-menu-end p-0 ${isOpen ? 'show' : ''}`} 
        style={{ 
          width: '320px', 
          maxHeight: '400px', 
          overflowY: 'auto',
          display: isOpen ? 'block' : 'none'
        }}
      >
        <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
          <h6 className="mb-0">Notifications</h6>
          <div>
            {notifications.length > 0 && (
              <button 
                className="btn btn-sm btn-link text-decoration-none" 
                onClick={clearAllNotifications}
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        
        {notifications.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">No notifications</p>
          </div>
        ) : (
          <div>
            {notifications.map(notification => (
              <div key={notification.id} className={`border-bottom notification-item p-2 ${notification.read ? '' : 'bg-light'}`}>
                <div className="d-flex">
                  <div className="me-2">
                    <i className={`${getIcon(notification.type)}`}></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">{formatTime(notification.timestamp)}</small>
                      <button 
                        className="btn btn-sm btn-link p-0 text-muted" 
                        onClick={() => clearNotification(notification.id)}
                        aria-label="Dismiss notification"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                    <p className="mb-1">{notification.message}</p>
                    {notification.displayId && notification.displayName && (
                      <Link to={`/my-displays/${notification.displayId}`} className="btn btn-sm btn-outline-primary">
                        View Display: {notification.displayName}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisplayNotifications; 